import { AxiosInstance } from 'axios';
import { axiosExternalRecourseFactory, axiosFactory } from '../../../../client';
import * as ROUTES from './routes';
import {
  AccessArrangementAcknowledgementResponse,
  AccessArrangementGetReviewAmendment,
  AccessArrangementGetReviewDetails,
  AccessArrangementRequestInfoModel,
  AccessArrangmentGetMedicalConditionsResponse,
  AccessArrangmentGetSelectedResponse,
  AccessArrangmentGetSummaryDetails,
  AccessArrangmentGetSummaryPDF,
  AccessArrangmentPostCreateApplication,
  AccessArrangmentPostModifyDraftRequestData,
  AccessArrangmentPostUploadDocumentsRequest,
  MedicalConditionAmendment,
  UpdateAccessArrangementData,
} from '../../model';
import { AccessArrangementRequestData } from '../../../../../modules/accessArrangment/components/candidate/AccessArrangementRequest/AccessArrangementRequestModels';
import { ApiPathParam } from '../../../../../models/SearchParamSchema';
import { AccessArrangementResultListing } from '../../../../../modules/accessArrangment/components/candidate/AccessArrangements/AccessArrangementsModels';

const baseURL = `${import.meta.env.VITE_APP_API_DOMAIN}/candidate/api/access-arrangements`;
const timeout = 10000;

export const externalUploadClient = axiosExternalRecourseFactory(``, timeout);

export const accessArrangement = axiosFactory(baseURL, timeout);

export const accessArrangementTestCandidateClient = (accessArrangement as unknown) as AxiosInstance;

/**
 * to get access arrangement request
 */
export const getAccessArrangementRequest = (key: string | undefined) => {
  const params: { [key: string]: string } = {};
  if (key) {
    params.key = key;
  }

  return accessArrangement.get<AccessArrangementResultListing>(
    ROUTES.GET_ACCESS_ARRANGEMENT_REQUEST,
    params
  );
};

/**
 * to delete access arrangement request
 */
export const deleteAccessArrangementRequest = (csrfToken: string, uuid: string) =>
  accessArrangement.delete<undefined>(csrfToken, ROUTES.DELETE_ACCESS_ARRANGEMENT_REQUEST(uuid));

/** to get appeal exam level fees
 * */
export const postAccessArrangementDraft = (
  tokenCsrf: string,
  data: AccessArrangmentPostCreateApplication
) =>
  accessArrangement.post<AccessArrangmentPostCreateApplication>(
    tokenCsrf,
    `${ROUTES.POST_ACCESS_ARRANGEMENT_POST_DRAFT_APPLICATION}`,
    { uuid: data.uuid }
  );
/** to get appeal exam level fees
 * */
export const postAccessArrangementNewApplication = (tokenCsrf: string) =>
  accessArrangement.post<AccessArrangmentPostCreateApplication>(
    tokenCsrf,
    `${ROUTES.POST_ACCESS_ARRANGEMENT_NEW_DRAFT_APPLICATION}`,
    {}
  );

export const postAccessArrangementCoordinatorNewApplication = (tokenCsrf: string, data: any) =>
  accessArrangement.post<AccessArrangmentPostCreateApplication>(
    tokenCsrf,
    `${ROUTES.POST_ACCESS_ARRANGEMENT_COORDINATOR_NEW_DRAFT_APPLICATION(data.uuid)}`,
    {}
  );

export const getAccessArrangementMedicalCondtition = () =>
  accessArrangement.get<AccessArrangmentGetMedicalConditionsResponse>(
    `${ROUTES.GET_ACCESS_ARRANGEMENT_MEDICAL_CONDITIONS}`
  );
export const getAccessArrangemenDraftDateTime = (uuid: string) =>
  accessArrangement.get(`${ROUTES.GET_ACCESS_ARRANGEMENT_DRAFT_DATE_TIME(uuid)}`);

export const getAccessArrangementSelectedCondtition = (uuid: string) =>
  accessArrangement.get<AccessArrangmentGetSelectedResponse>(
    ROUTES.GET_ACCESS_ARRANGEMENT_SELECTED_CONDITIONS(uuid)
  );

export const postAccessArrangementModifySelectedMedicalConditions = (
  tokenCsrf: string,
  data: AccessArrangmentPostModifyDraftRequestData
) =>
  accessArrangement.post<AccessArrangmentPostModifyDraftRequestData>(
    tokenCsrf,
    `${ROUTES.POST_ACCESS_ARRANGEMENT_MODIFY_SELECTED_MEDICAL_CONDITIONS(data.uuid)}`,
    data.payload
  );

export const deleteAccessArrangementFile = (tokenCsrf: string, uuid: string) =>
  accessArrangement.delete<string>(tokenCsrf, `${ROUTES.DELETE_ACCESS_ARRANGEMENT_FILE(uuid)}`);

export const getAccessArrangementRequestDetails = (uuid: string, paging: ApiPathParam) =>
  accessArrangement.get<AccessArrangementRequestInfoModel>(
    ROUTES.GET_ACCESS_ARRANGEMENT_REQUEST_DETAILS(uuid, paging)
  );

export const postAccessArrangementRequestDetails = (
  csrfToken: string,
  data: { uuid: string } & { accessArrangementRequestData: AccessArrangementRequestData }
) =>
  accessArrangement.post<undefined>(
    csrfToken,
    ROUTES.POST_ACCESS_ARRANGEMENT_REQUEST_DETAILS(data.uuid),
    data.accessArrangementRequestData
  );

export const putAccessArrangementRequestDetails = (
  csrfToken: string,
  data: { uuid: string } & { accessArrangementRequestData: AccessArrangementRequestData }
) =>
  accessArrangement.put<undefined>(
    csrfToken,
    ROUTES.PUT_ACCESS_ARRANGEMENT_REQUEST_DETAILS(data.uuid),
    data.accessArrangementRequestData
  );

export const deleteAccessArrangementRequestDetails = (
  csrfToken: string,
  data: { uuid: string; detailUuid: string }
) =>
  accessArrangement.delete<undefined>(
    csrfToken,
    ROUTES.DELETE_ACCESS_ARRANGEMENT_REQUEST_DETAILS(data.uuid, data.detailUuid)
  );
export const postAccessArrangementUploadDocuments = (
  tokenCsrf: string,
  payload: AccessArrangmentPostUploadDocumentsRequest
) =>
  accessArrangement.post<AccessArrangmentPostUploadDocumentsRequest>(
    tokenCsrf,
    `${ROUTES.POST_ACCESS_ARRANGEMENT_UPLOAD_DOCUMENTS(payload.uuid)}`,
    payload.payload
  );

/** to upload file to s3
 * */
export const putUploadFileToS3 = (data: File | FormData, putUrl: string) =>
  externalUploadClient.put<any>(putUrl, data);

/** to get review data
 * */
export const getReviewApplicationData = (uuid: string) =>
  accessArrangement.get<AccessArrangementGetReviewDetails>(
    ROUTES.GET_ACCESS_ARRANGEMENT_REVIEW_DETAILS(uuid)
  );

/** to save draft
 * */
export const putReviewApplicationData = (
  csrfToken: string,
  updateAccessArrangementData: UpdateAccessArrangementData
) =>
  accessArrangement.put<undefined>(
    csrfToken,
    ROUTES.PUT_ACCESS_ARRANGEMENT_UPDATE_DRAFT(updateAccessArrangementData)
  );

/** to get review data
 * */
export const getRequestAcknoledgment = (uuid: string) =>
  accessArrangement.get<AccessArrangementAcknowledgementResponse>(
    ROUTES.GET_ACCESS_ARRANGEMENT_REQUEST_ACKNOWLEDGEMENT(uuid)
  );

export const getRequestSummary = (uuid: string) =>
  accessArrangement.get<AccessArrangmentGetSummaryDetails>(
    ROUTES.GET_ACCESS_ARRANGEMENT_SUMMARY(uuid)
  );

export const getSummaryPdf = (uuid: string) =>
  accessArrangement.get<AccessArrangmentGetSummaryPDF>(ROUTES.GET_ACCESS_ARRANGEMENT_PDF(uuid));

export const getMedicalConditionAmendment = (uuid: string) =>
  accessArrangement.get<MedicalConditionAmendment>(ROUTES.GET_MEDICAL_CONDITIONS_AMENDMENT(uuid));

export const getAccessArrangementMedicalConditionAmendment = (uuid: string) =>
  accessArrangement.get<AccessArrangmentGetMedicalConditionsResponse>(
    ROUTES.GET_ACCESS_ARRANGEMENT_MEDICAL_CONDITIONS_AMENDMENT(uuid)
  );

export const getReviewAmendmentApplicationData = (uuid: string) =>
  accessArrangement.get<AccessArrangementGetReviewAmendment>(
    ROUTES.GET_ACCESS_ARRANGEMENT_REVIEW_DETAILS_AMENDMENT(uuid)
  );

export const getIndividualReportFiles = (uuid: string) =>
  accessArrangement.get<{ fileUuid: string; fileName: string; fileSizeInBytes: number }[]>(
    ROUTES.GET_ACCESS_ARRANGEMENT_INDIVIDUAL_REPORT_FILES(uuid)
  );

export const getIndividualReportTemplate = () =>
  accessArrangement.get<{ url: string }>(
    ROUTES.GET_ACCESS_ARRANGEMENT_INDIVIDUAL_REPORT_TEMPLATE
  );

export const getIndividualReportSubmittedFiles = (uuid: string) =>
  accessArrangement.get<{ fileUuid: string; fileName: string; fileSizeInBytes: number }[]>(
    ROUTES.GET_ACCESS_ARRANGEMENT_INDIVIDUAL_REPORT_SUBMITTED_FILES(uuid)
  );

